/**
 * The rear-camera request shared by every kiosk surface that opens a camera
 * (the barcode scanner, the G-80 photo search). One place, so the two never
 * ask for different resolutions and get different focus behaviour.
 *
 * Lives in utils rather than in `BarcodeScanner.tsx` because that component
 * is lazy-loaded together with the decoder; importing from it would pull the
 * whole decoder into any screen that only wants a camera.
 */

import { t } from "../i18n/t";

/**
 * `focusMode` / `torch` etc. are real MediaTrack capabilities but not yet in
 * the TS DOM lib. Extend the standard types so we can request continuous focus
 * without `any`. Unsupported keys are silently ignored by the browser.
 */
export interface ExtendedConstraintSet extends MediaTrackConstraintSet {
  focusMode?: string;
}
export interface ExtendedVideoConstraints extends MediaTrackConstraints {
  focusMode?: string;
  advanced?: ExtendedConstraintSet[];
}

/**
 * High-resolution rear-camera request. iOS Safari + many Android phones hand
 * back a low default (often 640×480) which leaves a small barcode too coarse
 * for the decoder to resolve. Asking for 1080p (downgraded gracefully
 * when unavailable) plus continuous focus dramatically improves decode rates.
 */
export const VIDEO_CONSTRAINTS: ExtendedVideoConstraints = {
  facingMode: { ideal: "environment" },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  focusMode: "continuous",
  advanced: [{ focusMode: "continuous" }],
};

/** Best-effort continuous autofocus; harmless no-op where unsupported (iOS). */
export async function tryApplyContinuousFocus(stream: MediaStream): Promise<void> {
  const [track] = stream.getVideoTracks();
  if (!track) return;
  try {
    await track.applyConstraints({
      advanced: [{ focusMode: "continuous" } as ExtendedConstraintSet],
    });
  } catch {
    /* Camera doesn't expose focusMode — ignore. */
  }
}

/** Stop every track so the camera light goes off the moment the screen closes. */
export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((track) => track.stop());
}

/** The sentence shown when the browser cannot open a camera at all. */
export function cameraUnsupportedMessage(): string {
  return t("scanner.noCameraSupport");
}

/** True when this browser can open a camera at all. */
export function cameraSupported(): boolean {
  return typeof navigator.mediaDevices?.getUserMedia === "function";
}

/** The one sentence a florist reads when the camera would not open. */
export function cameraErrorMessage(err: unknown): string {
  return err instanceof Error && /permission|denied|notallowed/i.test(err.name + err.message)
    ? t("scanner.permissionDenied")
    : t("scanner.couldNotStart");
}
